'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '@/components/public/i18n/LocaleProvider';
import Link from '@/components/public/i18n/LocalizedLink';
import { TEAM_CALENDAR } from '@/lib/public/ecosystem';
import PublicShell from './PublicShell';
import { Arrow } from './Icons';
import PortalDigitalIntro from './PortalDigitalIntro';
import PortalCinematicGallery, { type GalleryHandle } from './PortalCinematicGallery';
import EcosystemMap from './EcosystemMap';
import { HomeTestimonials } from '../testimonials/Testimonials';
import { ProjectInvitation } from './EditorialPage';
import './portal.css';
import './ecosystem-home.css';

const INTRO_KEY = 'rdv.intro.seen.v2';
const NEEDS = [
  { href: '/r-node', label: 'Relier mes ventes, mes équipes et mes opérations', destination: 'R-Node' },
  { href: '/rise/decouvrir', label: 'Mieux accueillir mes prospects et mes partenaires', destination: 'RISE' },
  { href: '/elina', label: 'Mobiliser notre savoir-faire pour mieux décider', destination: 'Élina' },
  { href: '/methode-sx', label: 'Traduire votre intelligence collective', destination: 'Méthode SX' },
];

export default function UniversePortal() {
  const { t, content } = useI18n();
  const [intro, setIntro] = useState(false);
  const [paused, setPaused] = useState(false);
  const title = useRef<HTMLHeadingElement>(null);
  const replay = useRef<HTMLButtonElement>(null);
  const gallery = useRef<GalleryHandle>(null);
  const replaying = useRef(false);
  const finish = useCallback(() => {
    try { sessionStorage.setItem(INTRO_KEY, '1'); } catch { /* Optional session storage. */ }
    setIntro(false);
    (replaying.current ? replay.current : title.current)?.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem(INTRO_KEY) === '1'; } catch { /* Optional session storage. */ }
    if (!seen && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !window.location.hash) setIntro(true);
  }, []);

  return <PublicShell portal>
    <main id="contenu" className="eco-home">
      <section className="u-portal-stage eco-home-stage" aria-labelledby="home-title">
        <div className="eco-home-copy">
          <p className="u-eyebrow"><span className="eco-origin-dot" />{t('Votre métier, au cœur du digital')}</p>
          <h1 id="home-title" ref={title} tabIndex={-1}>{t('Votre savoir-faire devient')} <span>{t('le moteur digital de votre activité')}</span></h1>
          <p className="eco-home-lead">{t('RDV-Partners conçoit une plateforme qui traduit votre expertise métier pour coordonner le travail de vos équipes, mieux servir vos clients et éclairer vos décisions.')}</p>
          <p className="eco-home-method">{t('Grâce à notre méthode SX, vos pratiques, vos règles et vos ambitions prennent forme dans R-Node, le noyau digital de votre entreprise.')}</p>
          <div className="eco-home-actions"><Link href="/elina/decouvrir?contexte=accueil#echanger" className="u-button">{t('Étudier mon projet avec Élina')}<Arrow /></Link><Link href="/ecosysteme" className="eco-text-link">{t('Comprendre notre écosystème')}<Arrow diagonal /></Link></div>
          <a className="eco-human-link" href={TEAM_CALENDAR} target="_blank" rel="noopener noreferrer">{t('Échanger avec notre équipe')}</a>
        </div>
        <div className="eco-home-map"><EcosystemMap paused={intro || paused} onSelect={index => gallery.current?.select(index)} /></div>
        <PortalCinematicGallery ref={gallery} paused={paused} intro={intro} onPause={() => setPaused(value => !value)} />
        <div className="eco-home-bottom"><a href="#vos-besoins">{t('Partir de mon besoin')} <span aria-hidden="true">↓</span></a><button ref={replay} type="button" onClick={() => { replaying.current = true; setIntro(true); }}>{t('Revoir l’introduction ')}<span aria-hidden="true">↻</span></button></div>
        {intro && <PortalDigitalIntro onFinished={finish} />}
      </section>
      <nav className="eco-home-needs" id="vos-besoins" aria-label={t('Choisissez votre priorité')}>{content(NEEDS).map((need, index) => <Link href={need.href} key={need.href}><small>0{index + 1} · {need.destination}</small><span>{need.label}</span><Arrow diagonal /></Link>)}</nav>
      <section className="eco-home-trust" aria-labelledby="home-trust-title"><div><p className="u-eyebrow">{t('Notre façon de construire')}</p><h2 id="home-trust-title">{t('Nous construisons à partir de votre réalité')}</h2><p>{t('Nous travaillons avec votre direction et vos équipes pour comprendre ce qui fait la qualité de votre métier, concevoir les parcours utiles et vérifier leur fonctionnement avec vous.')}</p><Link href="/methode-sx" className="eco-text-link">{t('Découvrir la méthode SX')}<Arrow /></Link></div><HomeTestimonials /></section>
      <ProjectInvitation />
    </main>
  </PublicShell>;
}


